import { Container } from '@gasbuddy/react-components';
import loadable from '@loadable/component';
import classnames from 'classnames/bind';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';
import useGeoIpInfo from '../../../lib/hooks/useGeoIpInfo';
import useScreenView from '../../../lib/hooks/useScreenView';
import { COUNTRY_CODES, SUPPORTED_LANGUAGES } from '../../constants';
import { DISCLAIMER_EFFECTIVE_DATES, PRIVACY_EFFECTIVE_DATES, TERMS_EFFECTIVE_DATE } from '../../constants/legal';
import LegalPageHeader from '../LegalPageHeader';
import Page from '../Page';
import styles from './LegalPage.module.css';

const cx = classnames.bind(styles);

const DEFAULT_TERMS = 'CONTEST';

const LEGAL_CONTENT_MAP = {
  PRIVACY: {
    US: loadable(() => import('./PrivacyPolicies/USA')),
    CA: loadable(() => import('./PrivacyPolicies/Canada')),
    CA_FR: loadable(() => import('./PrivacyPolicies/Canada_FR')),
  },
  DISCLAIMER: {
    US: loadable(() => import('./Disclaimers/USA')),
    CA: loadable(() => import('./Disclaimers/Canada')),
    CA_FR: loadable(() => import('./Disclaimers/Canada_FR')),
  },
  TERMS: {
    CONTEST: loadable(() => import('./Terms/Contest')),
    CONTEST_FR: loadable(() => import('./Terms/Contest_FR')),
    RECEIPT: loadable(() => import('./Terms/ReceiptScan')),
  },
};

export default function LegalPage() {
  const { t, i18n } = useTranslation('miscellaneous');
  const { identifier: identifierInUrl } = useParams();
  const location = useLocation();
  const hasIdentifierInPath = !!identifierInUrl;

  // For now, we can assume that Disclaimer countries and Privacy countries are the same
  const supportedCountries = Object.keys(COUNTRY_CODES).map(countryName => countryName.substring(0, 3));
  const isSupportedCountry = hasIdentifierInPath && supportedCountries.includes(identifierInUrl.toUpperCase());

  const supportedTerms = Object.keys(TERMS_EFFECTIVE_DATE);
  const isSupportedTerms = hasIdentifierInPath && supportedTerms.includes(identifierInUrl.toUpperCase());

  // Determine content type and canonical path from path pattern /${legal page}/:countryCode
  const contentType = location.pathname.replace(`/${identifierInUrl}`, '').replace(/\//g, '').toLowerCase();
  const isTermsPage = contentType === 'terms';

  if (hasIdentifierInPath && ((!isTermsPage && !isSupportedCountry) || (isTermsPage && !isSupportedTerms))) {
    const error = new Error(`Unsupported legal page: ${contentType}/${identifierInUrl}.`);
    error.status = 404;
    throw error;
  }

  const countryCode = identifierInUrl?.substring(0, 2)?.toUpperCase();
  const country = isSupportedCountry && countryCode;
  const geoIp = useGeoIpInfo();
  const identifier = isTermsPage ? (isSupportedTerms ? identifierInUrl : DEFAULT_TERMS).toUpperCase() : (country || geoIp.country);

  // TODO: Generalize this when we support more than just french
  const useFrench = i18n.language === SUPPORTED_LANGUAGES.FRENCH;
  const LegalContent = useFrench && LEGAL_CONTENT_MAP[contentType.toUpperCase()]?.[`${identifier}_FR`]
    ? LEGAL_CONTENT_MAP[contentType.toUpperCase()][`${identifier}_FR`]
    : LEGAL_CONTENT_MAP[contentType.toUpperCase()]?.[identifier];

  const isDisclaimer = contentType === 'disclaimer';
  let tableOfContents;
  if (isDisclaimer && identifier?.toLowerCase() === COUNTRY_CODES.USA.toLowerCase()) {
    tableOfContents = (
      <Fragment>
        <h3>
          TABLE OF CONTENTS
        </h3>
        <ol>
          <li>
            <a href="#terms-of-service" rel="noopener noreferer">
              GASBUDDY - TERMS OF SERVICE
            </a>
          </li>
          <li>
            <a href="#rewards-terms" rel="noopener noreferer">
              GASBUDDY<sup>&reg;</sup> MASTER REWARDS TERMS & CONDITIONS
            </a>
          </li>
        </ol>
        <br />
        <br />
      </Fragment>
    );
  }

  let screenName;
  let title;
  let effectiveDate;
  let showCountryLinks = true;

  switch (contentType) {
    case 'disclaimer':
      screenName = 'Disclaimer';
      title = t('disclaimerTitle', { ns: 'legal' });
      effectiveDate = DISCLAIMER_EFFECTIVE_DATES[identifier];
      break;

    case 'terms':
      screenName = 'Terms';
      title = t(`${identifier.toLowerCase()}Title`, { ns: 'legal' });
      effectiveDate = TERMS_EFFECTIVE_DATE[identifier];
      showCountryLinks = false;
      break;

    case 'privacy':
      screenName = 'Privacy Policy';
      title = t('privacyTitle', { ns: 'legal' });
      effectiveDate = PRIVACY_EFFECTIVE_DATES[identifier];
      break;

    // Note: Router should not send any other path to LegalPage, but this block is here for completeness
    default: {
      const error = new Error(`Unknown legal content type: ${contentType}.`);
      error.status = 404;
      throw error;
    }
  }

  let canonicalPathExtension = '';
  if (!isTermsPage) {
    // rely on geo country if no country code in path
    canonicalPathExtension = isSupportedCountry ? `/${identifierInUrl}` : `/${identifier}`;
  } else if (isTermsPage && hasIdentifierInPath) {
    // canonical for /terms/contest is /terms, otherwise use identifier
    canonicalPathExtension = !isSupportedTerms || identifierInUrl.toLowerCase() === DEFAULT_TERMS.toLowerCase() ? '' : `/${identifierInUrl}`;
  }
  const canonicalPath = `/${contentType}${canonicalPathExtension}`;

  useScreenView(screenName, {
    country: !isTermsPage ? identifier : undefined,
    termsType: isTermsPage ? identifier : undefined,
  });

  return (
    <Page
      compact
      logScreenView={false}
    >
      <Container className={cx('legalPage')}>
        <LegalPageHeader
          canonicalPath={canonicalPath}
          country={showCountryLinks ? identifier : undefined}
          effectiveDate={effectiveDate}
          renderCountrySpecificTitle={!isTermsPage}
          title={title}
          tableOfContents={tableOfContents}
        />
        <LegalContent />
      </Container>
    </Page>
  );
}
